import React from 'react';
import { ShieldCheck, Zap, Users, Globe } from 'lucide-react';
import { REGIONS, PORTFOLIO, WHY } from '../mock';
import Reveal from './Reveal';

const ICONS = { ShieldCheck, Zap, Users, Globe };
const REGION_COLOR = {
  lime: 'text-[#5bd0ff] border-[#5bd0ff]/40',
  cyan: 'text-[#0ef3fe] border-[#0ef3fe]/40',
  magenta: 'text-[#088ffe] border-[#088ffe]/40',
};

export default function Showcase() {
  return (
    <>
      {/* GLOBAL REACH */}
      <section id="reach" className="relative py-24 px-5 grid-bg">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <span className="font-mono-accent text-xs text-[#0ef3fe]">// GLOBAL REACH</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Delivering Across Continents</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {REGIONS.map((r, i) => (
              <Reveal key={r.name} delay={i * 120}>
                <div className={`group h-full overflow-hidden neon-border rounded-3xl border ${REGION_COLOR[r.color]} hover:bg-white/[0.03] transition-colors`}>
                  <div className="relative h-44 overflow-hidden">
                    <img src={r.image} alt={r.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1526] via-[#0a1526]/30 to-transparent" />
                    <span className="absolute top-4 left-5 font-mono-accent text-5xl font-bold text-white/25">0{i + 1}</span>
                  </div>
                  <div className="p-8">
                    <h3 className="font-display text-2xl font-bold mb-2 text-white">{r.name}</h3>
                    <p className="text-sm text-white/55 mb-6 leading-relaxed">{r.desc}</p>
                    <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-semibold ${REGION_COLOR[r.color]}`}>
                      {r.metric}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section id="work" className="relative py-24 px-5">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <span className="font-mono-accent text-xs text-[#088ffe]">// PORTFOLIO</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Our Work in Action</h2>
              <p className="text-white/55 mt-4">Real projects, real results.</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5 auto-rows-[220px]">
            {PORTFOLIO.map((p, i) => (
              <Reveal key={p.title} delay={i * 90} className={p.big ? 'md:col-span-2 md:row-span-2' : ''}>
                <div className="group relative h-full w-full rounded-3xl overflow-hidden neon-border">
                  <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1526] via-[#0a1526]/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <span className="font-mono-accent text-[10px] text-white">{p.cat.toUpperCase()}</span>
                    <h3 className="font-display text-xl font-bold mt-1">{p.title}</h3>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="relative py-24 px-5 grid-bg">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <span className="font-mono-accent text-xs text-[#5bd0ff]">// WHY ELECTRONICO</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Excellence, Engineered</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map((w, i) => {
              const Icon = ICONS[w.icon];
              return (
                <Reveal key={w.title} delay={i * 100}>
                  <div className="group h-full overflow-hidden neon-border rounded-3xl hover:-translate-y-2 transition-transform duration-300">
                    <div className="relative h-40 overflow-hidden">
                      <img src={w.image} alt={w.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1526] via-[#0a1526]/40 to-transparent" />
                      <span className="absolute bottom-3 left-4 h-12 w-12 rounded-2xl bg-[#0a1526]/70 backdrop-blur-md flex items-center justify-center group-hover:glow-cyan transition-shadow">
                        <Icon className="h-6 w-6 text-[#0ef3fe]" />
                      </span>
                    </div>
                    <div className="p-6">
                      <h3 className="font-display text-lg font-bold mb-2">{w.title}</h3>
                      <p className="text-sm text-white/55 leading-relaxed">{w.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
